/**
 * Protected Route Component
 * Redirects unauthenticated users to login page
 * Redirects users without MFA to setup-mfa page (MFA is mandatory)
 * Redirects users with expired trials to upgrade page
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/lib/auth-context';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { Loader2 } from 'lucide-react';
import TrialExpiredPage from '@/pages/trial-expired';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, checkMFAStatus } = useAuth();
  const [location, setLocation] = useLocation();
  const trialStatus = useTrialStatus();
  const [mfaChecked, setMfaChecked] = useState(false);
  const [hasMFA, setHasMFA] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Save the current location to redirect back after login
      const returnTo = location !== '/login' ? location : '/dashboard';
      sessionStorage.setItem('returnTo', returnTo);
      setLocation('/login');
    }
  }, [isLoading, isAuthenticated, location, setLocation]);

  // Check MFA status after authentication
  useEffect(() => {
    const verifyMFA = async () => {
      if (isAuthenticated && !isLoading) {
        try {
          const mfaStatus = await checkMFAStatus();
          setHasMFA(mfaStatus.enabled);
          setMfaChecked(true);

          // Redirect to MFA setup if not enrolled
          if (!mfaStatus.enabled && location !== '/setup-mfa') {
            // Save the current location to redirect back after MFA setup
            const returnTo = location !== '/setup-mfa' ? location : '/dashboard';
            sessionStorage.setItem('returnTo', returnTo);
            setLocation('/setup-mfa');
          }
        } catch (error) {
          console.error('Error checking MFA status:', error);
          setMfaChecked(true);
          // On error, allow access but log the issue
        }
      }
    };

    verifyMFA();
  }, [isAuthenticated, isLoading, checkMFAStatus, location, setLocation]);

  // Show loading while checking auth or MFA
  if (isLoading || trialStatus.isLoading || (isAuthenticated && !mfaChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // MFA not enabled - will be redirected by useEffect
  if (!hasMFA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <p className="text-sm text-gray-600">Checking security settings...</p>
        </div>
      </div>
    );
  }

  // Show trial expired page if trial has ended and no active subscription
  if (trialStatus.isExpired && !trialStatus.hasActiveSubscription) {
    return <TrialExpiredPage />;
  }

  return <>{children}</>;
}
